import { DOCUMENT } from '@angular/common';
import { inject, Injectable, signal } from '@angular/core';
import { environment } from '@environments/environment';
import { CURRENT_LANGUAGE } from '@shared/services/language';
import { LogsService } from '@shared/services/logs';
import { Observable, of } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class DidomiService {
  readonly sdkLoaded = signal(false);
  private readonly doc = inject(DOCUMENT);
  private readonly logs = inject(LogsService);

  /**
   * Loads Didomi SDK once and hints the browser to preconnect to the CMP origin.
   * Preconnect covers DNS, TCP, and TLS for HTTPS origins; dns-prefetch is not needed for the same origin.
   * @returns Observable<boolean> indicating load status.
   */
  init(): Observable<boolean> {
    if (this.sdkLoaded()) return of(true);

    if (this.doc.getElementById('didomi-spc-loader')) {
      this.sdkLoaded.set(true);
      return of(true);
    }

    const didomi = environment.integrations?.didomi;
    if (!didomi) {
      this.sdkLoaded.set(true);
      return of(true);
    }

    const target = didomi.apiKeyByLanguage[CURRENT_LANGUAGE()];
    if (!target) {
      this.sdkLoaded.set(true);
      return of(true);
    }

    const sdkOrigin = 'https://sdk.privacy-center.org/';
    this.ensurePreconnect(sdkOrigin); // keep only preconnect, no dns-prefetch

    const scriptSrcBase = `${sdkOrigin}6a5f8f85-d9de-45e8-a616-267e43cf7a57/loader.js?target_type=notice&target=${target}`;

    const win = this.doc.defaultView as
      | (Window & {
          didomiConfig?: { sdkPath?: string; user?: { country?: string; region?: string } };
        })
      | null;

    if (win) {
      win.didomiConfig = win.didomiConfig || {};
      win.didomiConfig.sdkPath = win.didomiConfig.sdkPath || sdkOrigin;
    }

    let scriptSrc = scriptSrcBase;
    const user = win?.didomiConfig?.user;
    if (user?.country) {
      const params = new URLSearchParams();
      params.set('country', user.country);
      if (user.region) params.set('region', user.region);
      scriptSrc += `&${params.toString()}`;
    }

    const scriptEl = this.doc.createElement('script');
    scriptEl.id = 'didomi-spc-loader';
    scriptEl.type = 'text/javascript';
    scriptEl.async = true;
    scriptEl.src = scriptSrc;

    const firstScript = this.doc.getElementsByTagName('script')[0];
    if (firstScript?.parentNode) {
      firstScript.parentNode.insertBefore(scriptEl, firstScript);
    } else {
      this.doc.head.appendChild(scriptEl);
    }

    this.logs.info('DidomiService', 'Didomi SDK has been loaded');
    this.sdkLoaded.set(true);
    return of(true);
  }

  /**
   * Ensure a single preconnect link exists for the given origin.
   * Adds crossorigin for full handshake on HTTPS cross-origin connections.
   */
  private ensurePreconnect(href: string): void {
    const existing = Array.from(this.doc.querySelectorAll('link[rel="preconnect"]')).some(
      (l) => l.getAttribute('href') === href
    );
    if (existing) return;

    const linkEl = this.doc.createElement('link');
    linkEl.rel = 'preconnect';
    linkEl.href = href;
    linkEl.setAttribute('crossorigin', ''); // important for certain cross-origin cases
    this.doc.head.appendChild(linkEl);
  }
}
